const API_URL = 'https://api.dawwin.al-ilm.net'

export type ResearchEvidence = {
  id:string
  source:{title:string|null;doi:string|null;publicationYear:number|null;authors:Array<{name?:string;givenName?:string;familyName?:string}>;openAccess?:{isOpenAccess?:boolean;url?:string|null}|null}
  evidenceType:string
  relationship?:string|null
  topicFit?:{domain?:string}|null
  retrievalRelevance?:{score?:number;reasons?:string[]}|null
  abstractAvailable:boolean
  extracts:Array<{text:string;queryOverlap:number;basis:string}>
  limits:string[]
}
export type EvidenceSynthesis = {
  query:string
  synthesisMode:string
  evidence:ResearchEvidence[]
  evidenceMap:{themes:Array<{theme:string;retrievedPaperCount:number;evidenceIds:string[]}>;counts:Record<string,number>}
  sourceResearch:{evidenceGaps:Array<{type:string;message:string}>}
  guardrails:{statement:string}
}

export async function synthesiseEvidence(query:string):Promise<EvidenceSynthesis>{
  const response=await fetch(`${API_URL}/research/synthesis`,{
    method:'POST',credentials:'include',
    headers:{Accept:'application/json','Content-Type':'application/json'},
    body:JSON.stringify({query}),
  })
  const data=await response.json().catch(()=>null)
  // A real synthesis call can legitimately take well over a minute (see api/src/routes/
  // research.ts) -- the server distinguishes a timeout from a genuine failure via
  // data.timedOut, carried onto the thrown error so ResearchWorkspace.tsx can show an
  // accurate message ("try a narrower question") instead of a generic "unavailable" one.
  if(!response.ok)throw Object.assign(new Error(data?.error||`http_${response.status}`),{timedOut:Boolean(data?.timedOut)})
  return data as EvidenceSynthesis
}

// Lane Phase 1 (Dawwin Research's own /v1/lexicon/*, proxied server-to-server by Dawwin's own
// API at /research/lexicon/* -- see api/src/routes/research.ts). Shapes mirror Dawwin
// Research's lexicon-service.js exactly; no lookup/normalisation/coverage logic is
// reimplemented here.
export type LexiconProvenance={source:string;dataset:string;datasetVersion:string;datasetSnapshot:string;attribution:string;license:string}
export type LexiconRootRecord={root:string;transliteration:string|null;page:number|null}
export type LexiconHeadword={nodeId:string;arabic:string;transliteration:string|null;page:number|null;crossReferences:string[]}
export type LexiconCoverage={coverageStatus:'found'|'not_found';coverageVolume:'lane_primary'|'posthumous_supplement';coverageWarning:string|null}
export type LexiconHeadwordsResult=LexiconCoverage&{query:string;normalisedRoot:string;root:LexiconRootRecord|null;headwords:LexiconHeadword[];provenance:LexiconProvenance}

async function lexiconGet<T>(path:string,root:string):Promise<T>{
  const response=await fetch(`${API_URL}/research/${path}?root=${encodeURIComponent(root)}`,{credentials:'include',headers:{Accept:'application/json'}})
  const data=await response.json().catch(()=>null)
  if(!response.ok)throw new Error(data?.message||data?.error||`http_${response.status}`)
  return data as T
}
// Superset of /lexicon/root (also includes headwords), so this single call covers both the
// root record and its derivatives -- no need to call /lexicon/root separately.
export const listLexiconRootHeadwords=(root:string)=>lexiconGet<LexiconHeadwordsResult>('lexicon/root/headwords',root)

// Lane Phase 2 (Dawwin Research /v1/lexicon/entry and /v1/lexicon/search/*, same passthrough at
// /research/lexicon/*). An entry's text arrives as plain segments -- t text, gl Lane's glosses,
// ar Arabic, ref a cross-reference (nodeId only when the dataset resolves it) -- never markup.
export type LexiconSegment={k:'t'|'gl'|'ar';s:string}|{k:'ref';s:string;nodeId:string|null}
export type LexiconEntrySummary={nodeId:string;root:string;rootTransliteration:string|null;arabic:string;transliteration:string|null;verbForm:string|null;page:number|null}
export type LexiconEntry=LexiconEntrySummary&LexiconCoverage&{supplement:boolean;paragraphs:LexiconSegment[][];provenance:LexiconProvenance}
export type LexiconWordResult={query:string;normalisedWord:string;matchedWord:string|null;foldedHamza:boolean;coverageStatus:'found'|'not_found';roots:{root:string;transliteration:string|null;entries:number}[];entries:(LexiconEntrySummary&{match:'headword'|'form';forms?:string[]})[];totalEntries:number;provenance:LexiconProvenance}
export type LexiconEnglishResult={query:string;terms:string[];coverageStatus:'found'|'not_found';entries:(LexiconEntrySummary&{snippet:{k:'t'|'hit';s:string}[]})[];provenance:LexiconProvenance}

async function lexiconQuery<T>(path:string,param:'node'|'q',value:string):Promise<T>{
  const response=await fetch(`${API_URL}/research/${path}?${param}=${encodeURIComponent(value)}`,{credentials:'include',headers:{Accept:'application/json'}})
  const data=await response.json().catch(()=>null)
  if(!response.ok)throw new Error(data?.message||data?.error||`http_${response.status}`)
  return data as T
}
export const getLexiconEntry=(nodeId:string)=>lexiconQuery<LexiconEntry>('lexicon/entry','node',nodeId)
export const searchLexiconWord=(word:string)=>lexiconQuery<LexiconWordResult>('lexicon/search/word','q',word)
export const searchLexiconEnglish=(words:string)=>lexiconQuery<LexiconEnglishResult>('lexicon/search/english','q',words)

export type HadithAssessment={grader:string|null;grade:string;source:string|null;provider:string}
export type HadithRecord={
 id:string;provider:string;providerId:string|null;
 canonicalReference:{collection:string|null;collectionSlug:string|null;hadithNumber:string|null;bookNumber:string|number|null;bookHadithNumber:string|number|null};
 arabic:string|null;translation:{language:string|null;text:string}|null;isnad:string|null;sourceUrl:string|null;
 assessments:HadithAssessment[];grading:{filterCategories:string[];disagreement:boolean;assessmentVariation?:boolean};
 relatedAyah:string|null;provenance:Record<string,unknown>|null;contextTranslation?:{text:string;language:string;resolvedFrom:string;reason:string;provider:string}|null
}
export type HadithSearchResult={provider:string;query?:string;ayahKey?:string;grade:string;count?:number;page?:number;hasMore?:boolean;hadiths:HadithRecord[]}
export type HadithGradeFilter='all'|'sahih'|'hasan'|'daif'|'other-disputed'|'ungraded'

async function hadithGet(path:string,params:Record<string,string|number|boolean|undefined>):Promise<HadithSearchResult>{
 const qs=new URLSearchParams()
 Object.entries(params).forEach(([key,value])=>{if(value!==undefined&&value!==''&&value!==false)qs.set(key,String(value))})
 const response=await fetch(`${API_URL}/research/hadith/${path}?${qs}`,{credentials:'include',headers:{Accept:'application/json'}})
 const data=await response.json().catch(()=>null)
 if(!response.ok)throw new Error(data?.message||data?.error||`http_${response.status}`)
 return data as HadithSearchResult
}
export const searchHadith=(query:string,options:{limit?:number;collection?:string;grade?:HadithGradeFilter;includeArabic?:boolean}={})=>
 hadithGet('search',{q:query,...options})
export const relatedHadithByAyah=(ayahKey:string,options:{language?:string;limit?:number;grade?:HadithGradeFilter}={})=>
 hadithGet(`ayah/${encodeURIComponent(ayahKey)}`,options)

export async function transliterateHadithArabic(arabic:string):Promise<string>{
 const response=await fetch(`${API_URL}/research/hadith/transliterate`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({arabic})})
 const data=await response.json().catch(()=>null)
 if(!response.ok)throw new Error(data?.error||`http_${response.status}`)
 return String(data?.transliteration||'')
}

export async function suggestHadithArabicRoot(word:string):Promise<string>{
 const response=await fetch(`${API_URL}/research/hadith/root-suggestion`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({word})})
 const data=await response.json().catch(()=>null);if(!response.ok)throw new Error(data?.error||`http_${response.status}`);return String(data?.root||'')
}

export async function hadithEnglishReadingAid(arabic:string):Promise<string>{
 const response=await fetch(`${API_URL}/research/hadith/english-reading-aid`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({arabic})})
 const data=await response.json().catch(()=>null);if(!response.ok)throw new Error(data?.error||`http_${response.status}`);return String(data?.english||'')
}

export async function lookupHadithReference(collection:string,hadithNumber:string):Promise<HadithRecord>{
 const response=await fetch(`${API_URL}/research/hadith/reference/${encodeURIComponent(collection)}/${encodeURIComponent(hadithNumber)}`,{credentials:'include',headers:{Accept:'application/json'}})
 const data=await response.json().catch(()=>null)
 if(!response.ok)throw new Error(data?.message||data?.error||`http_${response.status}`)
 return data as HadithRecord
}

export async function askStudyNotes(question:string,contexts:Array<{id:string;title:string;text:string;kind?:'study_note'|'reflection'}>):Promise<{answer:string;sourceIds:string[]}>{
 const response=await fetch(`${API_URL}/research/ask-studies`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({question,contexts})});const data=await response.json().catch(()=>null);if(!response.ok)throw new Error(data?.error||`http_${response.status}`);return data as {answer:string;sourceIds:string[]}
}
